import { useMemo, useState } from "react";
import { z } from "zod";

import {
  FieldLanguageSelect,
  FieldSelect,
  Form,
  FormApplyOrCancel,
  FormikForm,
  FormRowField,
  Loading,
} from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";
import type { ContentLanguage, Language } from "@skilitics/nautilus-core-designer-data";

import { languageFormFormatStrings } from "./EditContentLanguage";

export type AddNewLanguageFormValues = {
  code: string;
  baseCode?: string | null;
};

type AddContentLanguageProps = {
  availableLanguages: ReadonlyArray<Language>;
  contentLanguages: ReadonlyArray<ContentLanguage>;
  onCancel: () => void;
  onSubmit: (formValues: AddNewLanguageFormValues) => void;
};

export const AddContentLanguage = ({
  availableLanguages,
  contentLanguages,
  onCancel,
  onSubmit,
}: AddContentLanguageProps) => {
  const formatMessage = useFormatMessage(languageFormFormatStrings);

  const [selectedLanguage, setSelectedLanguage] = useState("");

  const formContentLanguageSchema = useMemo(
    () =>
      z.object({
        code: z.string(),
        baseCode: z.string(),
      }),
    [],
  );

  if (!availableLanguages.length) {
    return <Loading />;
  }

  const availableOptions = availableLanguages.map(({ code, title }) => ({
    title,
    code,
    disabled: !!contentLanguages.find((language) => language.code === code),
  }));

  const baseOptions = [
    {
      label: formatMessage("noBaseLanguage"),
      value: "none",
    },
    ...(contentLanguages ?? [])
      .filter(({ code }) => code !== selectedLanguage)
      .map(({ language: { code, title } }) => ({
        label: `${code} - ${title}`,
        value: code,
      })),
  ];

  return (
    <div>
      <FormikForm<AddNewLanguageFormValues>
        initialValues={{ code: "", baseCode: "none" }}
        onSubmit={onSubmit}
        onReset={onCancel}
        formatStrings={languageFormFormatStrings}
        validationSchema={formContentLanguageSchema}
        dataTest="languages-form"
      >
        <Form>
          <FormRowField<AddNewLanguageFormValues> name="code">
            <FieldLanguageSelect
              languages={availableOptions}
              onSelectedOptionChange={({ value }) => setSelectedLanguage(value)}
            />
          </FormRowField>
          <FormRowField<AddNewLanguageFormValues> name="baseCode">
            <FieldSelect options={baseOptions} />
          </FormRowField>
          <FormApplyOrCancel applyLabel={formatMessage("addSubmitButtonLabel")} alwaysShow />
        </Form>
      </FormikForm>
    </div>
  );
};
